import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const RATINGS = [
  { band: "A", score: "92-100", colour: "bg-green-600", description: "Highest efficiency. New builds with solar panels, heat pumps, and triple glazing." },
  { band: "B", score: "81-91", colour: "bg-green-500", description: "Very efficient. Modern homes with excellent insulation and efficient heating." },
  { band: "C", score: "69-80", colour: "bg-lime-500", description: "Above average. Well-insulated properties with modern boilers." },
  { band: "D", score: "55-68", colour: "bg-yellow-400", description: "Average for UK housing stock. Most homes built after 1980." },
  { band: "E", score: "39-54", colour: "bg-amber-400", description: "Below average. Older properties with basic insulation." },
  { band: "F", score: "21-38", colour: "bg-orange-500", description: "Poor efficiency. Older homes with little or no insulation." },
  { band: "G", score: "1-20", colour: "bg-red-600", description: "Lowest rating. Uninsulated properties with old heating systems." },
]

const IMPROVEMENTS = [
  {
    title: "Loft insulation",
    cost: "\u00a3300-500",
    saving: "Up to \u00a3250/year",
    detail: "Adding or topping up loft insulation to 270mm is one of the cheapest and most effective improvements. It can improve an EPC by 5-10 points and is often eligible for government grants.",
  },
  {
    title: "Cavity wall insulation",
    cost: "\u00a3500-1,500",
    saving: "Up to \u00a3300/year",
    detail: "If the property has unfilled cavity walls (typically built 1920s-1990s), insulating them is highly cost-effective. A professional survey can confirm whether cavity wall insulation is suitable.",
  },
  {
    title: "Upgrade the boiler",
    cost: "\u00a32,000-4,000",
    saving: "Up to \u00a3350/year",
    detail: "Replacing an old G-rated boiler with a modern A-rated condensing boiler can significantly improve both the EPC rating and the property\u2019s heating costs. This is often the single biggest improvement available.",
  },
  {
    title: "Double glazing",
    cost: "\u00a33,000-8,000",
    saving: "Up to \u00a3200/year",
    detail: "Replacing single-glazed windows with double or triple glazing reduces heat loss and noise. The cost varies significantly by property size and window type. It typically improves the EPC by 5-10 points.",
  },
  {
    title: "LED lighting",
    cost: "\u00a3100-300",
    saving: "Up to \u00a365/year",
    detail: "Replacing all halogen and incandescent bulbs with LED alternatives is the cheapest improvement available. It contributes a small but easy EPC point gain and is something any seller can do before the assessment.",
  },
  {
    title: "Smart heating controls",
    cost: "\u00a3200-500",
    saving: "Up to \u00a3150/year",
    detail: "Adding a programmable thermostat, thermostatic radiator valves, and smart heating controls demonstrates efficient heating management. These are relatively cheap improvements that assessors will note.",
  },
]

const FAQ = [
  {
    q: "What is an EPC and do I need one to sell?",
    a: "An Energy Performance Certificate (EPC) rates a property\u2019s energy efficiency from A (most efficient) to G (least efficient). You are legally required to have a valid EPC before marketing a property for sale or rent in England and Wales. The certificate lasts 10 years. An estate agent cannot legally list your property without one.",
  },
  {
    q: "How much does an EPC cost?",
    a: "An EPC assessment typically costs \u00a360-120, depending on the property size and location. The assessment takes 45-90 minutes and must be carried out by a qualified Domestic Energy Assessor (DEA). Your estate agent can usually arrange one for you.",
  },
  {
    q: "Can I sell a property with a low EPC rating?",
    a: "Yes. There is currently no minimum EPC rating for selling a property in England and Wales (though there is a minimum of E for rental properties). However, buyers are increasingly aware of energy costs, and a low rating may affect their offer or willingness to proceed. Properties with higher ratings often sell faster and for more.",
  },
  {
    q: "Does EPC rating affect property value?",
    a: "Research suggests that properties with higher EPC ratings sell for a premium. A study by the Department for Energy Security found that moving from an EPC band D to C could add 3-5% to a property\u2019s value. With rising energy costs, this premium is likely to increase.",
  },
  {
    q: "What is the minimum EPC rating for rental properties?",
    a: "Since April 2020, rental properties in England and Wales must have a minimum EPC rating of E. Landlords cannot grant a new tenancy for a property rated F or G unless they have a valid exemption. Proposals to raise this minimum to C have been discussed but not yet implemented as of 2026.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function EpcRatingsExplainedGuide({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "EPC Ratings Explained: A Guide for Sellers and Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "EPC Ratings Explained" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            EPC ratings explained
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            What EPC ratings mean, why they matter when selling or renting,
            and practical ways to improve your property&rsquo;s energy
            efficiency rating before going to market.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            What is an EPC
            <span className="text-red">?</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              An Energy Performance Certificate (EPC) rates a
              property&rsquo;s energy efficiency on a scale from A (most
              efficient) to G (least efficient). It includes an estimated
              energy cost per year, a breakdown of the property&rsquo;s
              thermal performance, and recommendations for improvement.
            </p>
            <p>
              EPCs have been a legal requirement for property sales and
              lettings in England and Wales since 2008. You must have a
              valid EPC before marketing a property, and the certificate
              must be provided to potential buyers or tenants. An EPC
              lasts 10 years from the date of issue.
            </p>
            <p>
              The assessment is carried out by a qualified Domestic Energy
              Assessor (DEA) who inspects the property&rsquo;s
              construction, insulation, heating system, windows, and
              lighting. The result is a score from 1-100 and a
              corresponding letter band.
            </p>
          </div>
        </div>
      </section>

      {/* Ratings table */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            EPC rating bands
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            What each band means and typical properties in that category.
          </p>

          <div className="mt-6 space-y-2">
            {RATINGS.map((rating) => (
              <div
                key={rating.band}
                className="flex items-center gap-3 rounded-lg border border-stone bg-white p-3"
              >
                <span
                  className={`flex size-10 shrink-0 items-center justify-center rounded-lg text-base font-bold text-white ${rating.colour}`}
                >
                  {rating.band}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-charcoal">
                    Band {rating.band}{" "}
                    <span className="font-normal text-warm-grey">
                      ({rating.score} points)
                    </span>
                  </p>
                  <p className="text-xs text-slate">{rating.description}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-sm leading-relaxed text-slate">
            The average UK property has an EPC rating of D (score around
            60). New-build homes typically achieve B or C, while older
            period properties often sit at D or E without improvements.
          </p>
        </div>
      </section>

      {/* Why it matters */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why EPC ratings matter when selling
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Buyers are increasingly energy-conscious. Rising energy
              costs mean that a poorly insulated property with an old
              boiler carries a tangible ongoing expense that buyers factor
              into their offers. Research suggests properties rated C or
              above sell for 3-5% more than equivalent properties rated D
              or below.
            </p>
            <p>
              For agents, the EPC is a selling tool. A strong rating is
              worth highlighting in the property description, photography
              (showing modern insulation, double glazing, efficient
              heating), and video marketing. Conversely, a poor rating
              should be addressed proactively with buyers, ideally
              alongside a plan showing potential improvements and costs.
            </p>
            <p>
              For landlords, the situation is even more pressing. The
              current minimum EPC standard for rental properties is E,
              and further tightening to C is widely expected. Investing
              in energy efficiency now protects the property&rsquo;s
              long-term lettability.
            </p>
          </div>
        </div>
      </section>

      {/* Improvements */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How to improve your EPC rating
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            The most cost-effective improvements ranked by typical impact.
          </p>

          <div className="mt-6 space-y-4">
            {IMPROVEMENTS.map((item) => (
              <ScrollReveal key={item.title}>
                <div className="rounded-xl border border-stone bg-white p-5">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-base font-bold text-charcoal">
                      {item.title}
                    </h3>
                    <div className="shrink-0 text-right">
                      <p className="text-xs font-medium text-charcoal">
                        {item.cost}
                      </p>
                      <p className="text-xs text-warm-grey">
                        saves {item.saving}
                      </p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate">
                    {item.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <p className="mt-6 text-sm leading-relaxed text-slate">
            The EPC report itself includes tailored recommendations for
            the specific property, with estimated costs and savings. Use
            this as a starting point and prioritise improvements by
            cost-effectiveness.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "D", label: "average EPC rating for UK properties" },
            { stat: "3-5%", label: "price premium for C-rated vs D-rated homes" },
            { stat: "\u00a360-120", label: "typical cost of an EPC assessment" },
            { stat: "10 years", label: "validity period of an EPC certificate" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/selling-your-house",
                title: "Selling your house",
                desc: "The complete step-by-step guide to selling in the UK.",
              },
              {
                href: "/guides/estate-agent-fees",
                title: "Estate agent fees",
                desc: "Understand what agents charge and how to get value.",
              },
              {
                href: "/guides/marketing-rental-property",
                title: "Marketing rental properties",
                desc: "How to market a rental property effectively.",
              },
              {
                href: "/tools/stamp-duty-calculator",
                title: "Stamp duty calculator",
                desc: "Calculate your stamp duty liability quickly.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Market every property at its best
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Whether your listing scores an A or an E, great marketing
            makes every property shine. QuiteRight creates professional
            video ads from your listing photos in under 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
